<template>
  <Modal
    class="questions-dialog text-left"
    v-model="dialogShown"
    title="Self-review"
    @hide="handleDialogHide"
  >
    <div class="questions-content-wrapper">
      <div class="question">
        <h3 class="question-text">I {{ currentQuestion.wording }}</h3>
        <div class="answer">
          <VueSlider
            ref="slider"
            :min="1"
            :max="10"
            :interval="1"
            :lazy="true"
            :piecewise="true"
            :speed="0.3"
            :tooltip="sliderHasBeenTouched ? 'always' : false"
            :dot-size="40"
            :marks="true"
            :absorb="true"
            v-model="answers[currentQuestionIndex]"
            @change="sliderChanged"
          >
            <template v-slot:tooltip="tooltip">
              <span class="vue-slider-tooltip-top vue-slider-tooltip-wrap">
                <span class="vue-slider-tooltip">
                  {{ Math.round(+tooltip.value) }}
                </span>
              </span>
            </template>
          </VueSlider>
          <input
            test-id="question-hidden-input"
            style="visibility: hidden; display: none"
            type="text"
            v-model="answers[currentQuestionIndex]"
            aria-hidden="true"
          />
          <div class="scale">
            <span
              >Strongly <br />
              Disagree</span
            >
            <span>Disagree</span>
            <span>Agree</span>
            <span
              >Strongly <br />
              Agree</span
            >
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="questions-footer">
        <button
          class="btn btn-lg btn-primary"
          :class="{ invisible: currentQuestionIndex === 0 }"
          @click="previous"
          type="button"
        >
          Previous
        </button>
        <div class="questions-progress">{{ currentQuestionIndex + 1 }} / 6</div>
        <button
          class="btn btn-lg btn-primary"
          :disabled="!sliderHasBeenTouched"
          @click="next"
          test-id="questions-next-button"
          type="button"
        >
          {{ currentQuestionIndex === questions.length - 1 ? "Done" : "Next" }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import VueSlider from "vue-3-slider-component";
import { Modal } from "uiv";
import { debug as d } from "debug";
import { DevelopQuestion } from "../../services/cms/develop-question";
import { Analytics } from "../../services/analytics/analytics";

// import "vue-slider-component/dist-css/vue-slider-component.css";
// import "vue-slider-component/theme/default.css";

const debug = d("cs.QuestionsComponent");

const props = defineProps<{
  showModal: boolean;
  onSubmit: (answers: number[]) => void;
  onCancel: () => void;
  questions: DevelopQuestion[];
}>();

const choices = Array(10).map((_, i) => i + 1); // Numbers 1 - 10

const currentQuestionIndex = ref(0);

const initialValue = 5.5;

const answers = ref<Record<string, number>>({ 0: 1 });

const sliderHasBeenTouched = ref(false);

const dialogShown = ref(false);

const hasValidationError = ref(false);

watch(
  () => props.showModal,
  (newValue: boolean) => {
    dialogShown.value = newValue;
  },
);

const currentQuestion = computed(() => props.questions[currentQuestionIndex.value]);

const currentAnswer = computed(() => answers.value[currentQuestionIndex.value]);

function submit(): void {
  const answersArray = Object.values(answers.value);
  if (answersArray.every((value) => value > 0)) {
    hasValidationError.value = false;
    props.onSubmit(answersArray);
  } else {
    hasValidationError.value = true;
  }
}

function previous(): void {
  if (currentQuestionIndex.value > 0) {
    Analytics.action("Questionnaire Previous Question", "QuestionnaireQuestionActions", {
      fromQuestionNumber: currentQuestionIndex.value + 1,
    });
    currentQuestionIndex.value -= 1;
    sliderHasBeenTouched.value = true; // The previous question must have been answered
  }
}

function next(): void {
  if (currentQuestionIndex.value < props.questions.length - 1) {
    Analytics.action("Questionnaire Next Question", "QuestionnaireQuestionActions", {
      fromQuestionNumber: currentQuestionIndex.value + 1,
    });
    currentQuestionIndex.value += 1;
    sliderHasBeenTouched.value = !!currentAnswer.value && currentAnswer.value !== initialValue;
  } else {
    // Must be the last question
    submit();
  }
}

function cancel(): void {
  props.onCancel();
}

function handleDialogHide(): void {
  if (props.showModal) {
    cancel();
  }
}

function sliderChanged(value: number): void {
  sliderHasBeenTouched.value = true;
}

onMounted(() => {
  props.questions.forEach((q, i) => {
    answers.value[i] = initialValue;
  });
});
</script>

<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.questions-dialog {
  .vue-slider-mark-label {
    display: none;
  }

  @sliderSize: 6px;

  .vue-slider {
    height: @sliderSize !important;
  }

  .vue-slider-mark {
    width: @sliderSize !important;
    height: @sliderSize !important;
  }

  .questions-content-wrapper {
    // position: absolute;
    //top: 0;  // ?
    //bottom: 0; // ?
    //left: 0;
    //right: 0;
    //background-color: white;

    .question {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      user-select: none;

      .question-text {
        text-align: center;
        margin-bottom: 70px;
        min-height: 2.2em;
      }

      .question-answer {
        flex-shrink: 5;
        font-weight: 800;
        margin-bottom: 40px;
      }
    }

    .answer {
      width: 100%;
      padding-left: 10px;
      padding-right: 10px;

      .vue-slider {
        .vue-slider-process {
          background-color: @brand-primary;
        }

        .vue-slider-tooltip-wrap.vue-slider-tooltip-top {
          top: -15px;
        }

        .vue-slider-tooltip {
          font-size: 24px;
          padding-top: 6px;
        }
      }

      .scale {
        margin-top: 5px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;

        & > span {
          display: block;
          width: 8em;
          text-align: center;

          &:first-child {
            text-align: left;
          }

          &:last-child {
            text-align: right;
          }
        }
      }
    }
  }

  .modal-footer {
    background-color: #f5f5f5;

    .questions-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 5px 10px;

      .questions-progress {
        font-size: 24px;
      }

      .btn {
        width: 6em; // Fix width to ensure centering
      }
    }
  }
}
</style>
